import { auth } from "./auth";
import { buildApiUrl } from "@/config/env";

export interface RequestOptions extends Omit<RequestInit, "body"> {
  params?: object;
  body?: unknown;
  withAuth?: boolean;
}

export interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
  success?: boolean;
  show?: boolean;
}

export class RequestError extends Error {
  code?: number;
  response?: unknown;

  constructor(message: string, code?: number, response?: unknown) {
    super(message);
    this.name = "RequestError";
    this.code = code;
    this.response = response;
  }
}

const inFlightGetRequests = new Map<string, Promise<unknown>>();

const buildQueryString = (params?: object) => {
  if (!params) {
    return "";
  }

  const searchParams = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    searchParams.append(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

const createHeaders = (options: RequestOptions) => {
  const headers = new Headers(options.headers || {});
  const isFormDataBody = typeof FormData !== "undefined" && options.body instanceof FormData;

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  if (options.body !== undefined && !isFormDataBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (options.withAuth !== false) {
    const token = auth.getToken();
    if (token) {
      headers.set("Authorization", token);
      headers.set("token", token);
    }
  }

  return headers;
};

async function request<T>(path: string, options: RequestOptions = {}) {
  const url = `${buildApiUrl(path)}${buildQueryString(options.params)}`;
  const headers = createHeaders(options);
  const method = (options.method || "GET").toUpperCase();
  const isFormDataBody = typeof FormData !== "undefined" && options.body instanceof FormData;

  const execute = async () => {
    const response = await fetch(url, {
      ...options,
      headers,
      body:
        options.body === undefined
          ? undefined
          : isFormDataBody
            ? (options.body as FormData)
            : JSON.stringify(options.body),
    });

    if (!response.ok) {
      throw new RequestError(`HTTP ${response.status}`, response.status);
    }

    const result = (await response.json()) as ApiResponse<T>;
    if (result.code !== 0) {
      if (result.code === 1001) {
        auth.clearSession();
      }
      throw new RequestError(result.msg || "Request failed", result.code, result);
    }

    return result.data;
  };

  if (method !== "GET") {
    return execute();
  }

  const requestKey = [
    method,
    url,
    headers.get("Authorization") || "",
    headers.get("token") || "",
  ].join("::");
  const existing = inFlightGetRequests.get(requestKey);
  if (existing) {
    return existing as Promise<T>;
  }

  const pending = execute().finally(() => {
    inFlightGetRequests.delete(requestKey);
  });
  inFlightGetRequests.set(requestKey, pending);
  return pending as Promise<T>;
}

export const http = {
  get<T>(path: string, options?: RequestOptions) {
    return request<T>(path, { ...options, method: "GET" });
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>(path, { ...options, method: "POST", body });
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>(path, { ...options, method: "PUT", body });
  },

  delete<T>(path: string, options?: RequestOptions) {
    return request<T>(path, { ...options, method: "DELETE" });
  },
};
